/**
 * uPlot wrapper.
 *
 * WHY uPlot AND NOT A REACT CHART LIBRARY. A raw window on one unit is a point
 * every ten seconds; the API will hand back up to 200,000 of them. Recharts and
 * friends mount a React element per data point and fall over somewhere in the
 * low thousands — not slowly, but by locking the tab. uPlot draws to a canvas
 * and handles hundreds of thousands of points without complaint. That is the
 * whole reason for the dependency, and it is pinned to an exact version because
 * the chart is load-bearing.
 *
 * React's job here is to own the DOM node and nothing else: uPlot is imperative,
 * so the instance is created in an effect, fed with `setData` on update, and
 * destroyed on unmount. It is only RECREATED when something structural changes
 * (the series list, or the theme, since axis colours are baked in at
 * construction) — recreating on every data tick would throw away the user's zoom.
 */

import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';

export interface ChartSeries {
  /** Key into the API's `series` object. */
  key: string;
  label: string;
  /** CSS colour. Kept explicit rather than themed, so a series keeps its
   *  identity between light and dark. */
  stroke: string;
  fill?: string;
  /** Decimal places in the legend readout. */
  digits?: number;
}

interface Props {
  /** Epoch seconds, ascending. */
  t: number[];
  data: Record<string, (number | null)[]>;
  series: ChartSeries[];
  unit: string;
  height?: number;
  /** Forces a rebuild when the palette changes. */
  theme: string;
  /** Fix the y axis to 0..100 for percentages. */
  range?: [number, number];
}

function cssVar(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

export function Chart({ t, data, series, unit, height = 200, theme, range }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const plot = useRef<uPlot | null>(null);
  const structure = series.map((s) => s.key).join('|') + `|${theme}|${unit}`;

  // Build (and rebuild only on structural change).
  useEffect(() => {
    const node = host.current;
    if (!node) return;

    const axis = cssVar('--dim', '#888');
    const grid = cssVar('--line', '#ddd');
    const text = cssVar('--muted', '#666');

    const opts: uPlot.Options = {
      width: node.clientWidth || 800,
      height,
      // No animation and no cursor drag-to-zoom surprises; the window selector
      // above the chart is the one way the range changes, so what you see always
      // matches the label that says what you are looking at.
      cursor: { drag: { x: true, y: false }, focus: { prox: 24 } },
      legend: { live: true },
      scales: {
        x: { time: true },
        y: range ? { range: () => range } : {},
      },
      axes: [
        {
          stroke: axis,
          grid: { stroke: grid, width: 1 },
          ticks: { stroke: grid },
          font: '11px system-ui, sans-serif',
        },
        {
          stroke: axis,
          grid: { stroke: grid, width: 1 },
          ticks: { stroke: grid },
          font: '11px system-ui, sans-serif',
          label: unit,
          labelSize: 20,
          labelFont: '11px system-ui, sans-serif',
          size: 56,
        },
      ],
      series: [
        { label: 'time' },
        ...series.map((s) => ({
          label: s.label,
          stroke: s.stroke,
          fill: s.fill,
          width: 1.5,
          // Points only when they are far enough apart to be readable; at raw
          // resolution there are far too many for markers to mean anything.
          points: { show: (_u: uPlot, _i: number, i0: number, i1: number) => i1 - i0 < 60 },
          value: (_u: uPlot, v: number | null) =>
            v === null || v === undefined ? '—' : v.toFixed(s.digits ?? 0) + ' ' + unit,
          // Gaps stay gaps. A unit that stopped reporting for an hour must show
          // a hole, not a straight line pretending it kept going.
          spanGaps: false,
        })),
      ],
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (opts as any).axes[0].values = (
      _u: uPlot,
      splits: number[],
      _ai: number,
      _ss: number,
      space: number,
    ) => {
      const wide = space > 3600 * 12;
      return splits.map((s) =>
        new Date(s * 1000).toLocaleString(undefined, {
          month: wide ? 'short' : undefined,
          day: wide ? '2-digit' : undefined,
          hour: wide ? undefined : '2-digit',
          minute: wide ? undefined : '2-digit',
          hour12: false,
        }),
      );
    };

    const u = new uPlot(opts, [[], ...series.map(() => [])] as unknown as uPlot.AlignedData, node);
    plot.current = u;
    node.style.setProperty('--u-text', text);

    const observer = new ResizeObserver(() => {
      const w = node.clientWidth;
      if (w > 0) u.setSize({ width: w, height });
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      u.destroy();
      plot.current = null;
    };
  }, [structure, height, range, series, unit]);

  // Feed data. Separate from construction so a refresh keeps the zoom.
  useEffect(() => {
    const u = plot.current;
    if (!u) return;
    const aligned = [t, ...series.map((s) => data[s.key] ?? [])] as unknown as uPlot.AlignedData;
    u.setData(aligned);
  }, [t, data, series]);

  return <div className="chart-wrap" ref={host} />;
}

/**
 * Series colours.
 *
 * Chosen to stay distinguishable on both themes and to survive the common forms
 * of colour blindness: blue/orange/green rather than red/green, and the pair
 * most often plotted together (output and input power) are the two furthest
 * apart in hue.
 */
export const COLOURS = {
  out: '#2f7ed8',
  in: '#e07b39',
  pv: '#2ea36c',
  soc: '#8a5cd1',
  temp: '#c2483f',
  pack: '#6b7684',
} as const;
